import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { UploadError } from "../api/upload";
import { usePhoto } from "../auth/photo";
import { useEvent } from "../context/EventProvider";
import { logAction } from "../log/actionLog";
import { faceUploadLabel } from "../log/labels";
import { useT } from "../settings/context";
import { Button } from "../ui/Button";
import { Confirm } from "../ui/Confirm";
import { Icon } from "../ui/Icon";
import { Modal } from "../ui/Modal";
import { Cropper } from "./Cropper";
import {
  countMyPhotos,
  removeOwnPortrait,
  searchMyPhotos,
  setOwnPortrait,
} from "./face";
import { POLL_EVERY_MS, keepPolling } from "./poll";

/**
 * ═══ SA PHOTO, EN TÊTE DE L'ALBUM ═══
 *
 * L'album se remplit par reconnaissance faciale, et elle a besoin d'un portrait de
 * référence. Jusqu'ici seul le back office pouvait en poser un : un participant dont
 * personne n'avait chargé la photo voyait un album vide sans rien pouvoir y faire.
 *
 * Ici, il choisit la sienne, et la recherche part dans la foulée.
 *
 * ⚠️ TROIS TEMPS, UN SEUL GESTE. Poser la photo (rapide), chercher (quelques secondes),
 * puis GUETTER ce qui continue d'arriver. L'API n'attend que vingt secondes ; au-delà,
 * le traitement se poursuit de son côté, et demander un rechargement de page serait lui
 * faire porter notre limite. L'album se met donc à jour tout seul, tant que des photos
 * arrivent (voir `album/poll.ts` pour les bornes).
 *
 * ⚠️ ET ÇA SE VOIT AVANCER. Une attente sans signe est une attente qu'on abandonne : le
 * compte monte à mesure, et une barre avance tant qu'on guette.
 *
 * ⚠️ UNE LIGNE, ET RIEN QU'UNE. Ce bloc ACCOMPAGNE l'album, il ne s'annonce pas avant
 * lui : la pastille, ce qui se passe, et les gestes, tout sur le même rang et sur une
 * hauteur de pastille. Les deux gestes — changer, effacer — n'ont que leur dessin, leur
 * libellé en infobulle et pour les lecteurs d'écran.
 *
 * ⚠️ REPLIÉE QUAND LA PHOTO EST CONNUE ET QU'IL NE SE PASSE RIEN (2026-09-22). Pour qui
 * a déjà son portrait, cette ligne n'a rien à dire et rien à faire faire — elle prenait
 * pourtant la tête de l'album à chaque visite. Elle se réduit à un seul geste discret,
 * la pastille et « Gérer ma photo », qui la déplie. Dès qu'il se passe quelque chose
 * (envoi, recherche, effacement, résultat) elle se déplie d'elle-même : c'est là qu'elle
 * a quelque chose à raconter. Sans photo, elle est toujours dépliée : c'est à cette
 * personne-là qu'elle s'adresse.
 *
 * Le pli a existé une première fois et avait été retiré, pour que l'effacement — un
 * droit, pas une option avancée — reste visible sans le chercher. Il revient parce que
 * le coût d'une ligne permanente pour tout le monde a pesé plus lourd ; l'effacement
 * reste à UN clic, derrière un geste écrit et nommé, pas derrière un écran à deviner.
 *
 * ⚠️ PLUS DE BOUTON « RECONNAISSANCE PAR IA ». La recherche part d'elle-même après un
 * envoi de photo ; la relancer à la main sur une photo déjà connue a été retiré de
 * l'album (2026-09-22). Le bouton « Traitement IA » du back-office garde ce rôle, et
 * traite tout le domaine d'un coup.
 *
 * ⚠️ LA PHRASE FAIT PARTIE DE LA LIGNE. Sans photo, elle invite à en poser une ; avec,
 * elle dit à quoi sert celle-ci ; pendant un envoi, une recherche ou un effacement, ce qui
 * se passe. C'est le PLI, et non son silence, qui évite de prendre la tête de l'album pour
 * redire une consigne : dépliée à la demande, une ligne muette entre une pastille et deux
 * icônes paraissait vide (2026-09-22) — la même ligne que sans photo, moins la phrase.
 *
 * Les textes passent par `t()`, le français au point d'appel : ce que le site dit de
 * lui-même suit la langue lue.
 */

type State =
  | { step: "idle" }
  | { step: "sending" }
  | { step: "searching" }
  /** La recherche continue là-bas ; on guette ce qui arrive. `found` = ses photos, en tout. */
  | { step: "watching"; found: number }
  /** `found` = ses photos dans l'album, en tout, pas seulement celles de cette recherche. */
  | { step: "done"; found: number }
  | { step: "removing" }
  | { step: "removed" }
  | { step: "failed"; why: string };

export function Portrait({ userId }: { userId: number | undefined }) {
  const t = useT();
  const event = useEvent();
  const photo = usePhoto(userId);
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ step: "idle" });
  /**
   * LA PHOTO CHOISIE, TANT QU'ELLE N'EST PAS CADRÉE. Elle n'est pas encore partie : le
   * fichier attend dans la fenêtre de cadrage, et c'est le carré qui en sort qui monte.
   */
  const [picked, setPicked] = useState<File | null>(null);
  /** La question avant l'effacement — irréversible, donc jamais au premier clic. */
  const [asking, setAsking] = useState(false);
  /** La ligne dépliée à la demande — quand la photo est connue et qu'il ne se passe rien. */
  const [open, setOpen] = useState(false);

  /**
   * ⚠️ LA SURVEILLANCE VIT DANS UN EFFET, PAS DANS LE GESTIONNAIRE DU CLIC. Une boucle
   * lancée depuis le clic continuerait de tourner après que l'écran a été quitté, et
   * écrirait dans un composant démonté. Ici, `watching` est un ÉTAT : quitter l'album
   * arrête la minuterie avec le reste.
   */
  const watching = state.step === "watching";
  useEffect(() => {
    if (!watching || !event) return;
    let alive = true;
    const started = Date.now();
    let changedAt = Date.now();
    /**
     * ⚠️ LE POINT DE DÉPART EST LE COMPTE D'AVANT LA RECHERCHE, pas celui du premier
     * passage. Pris au premier passage, cinq secondes après, il englobait déjà les
     * photos que la recherche venait de rattacher : rien ne « montait », l'album n'était
     * jamais relu, et l'écran concluait « aucune photo » avec quatre photos en base.
     */
    let last: number = state.step === "watching" ? state.found : 0;
    let timer: number | undefined;

    const look = async () => {
      if (!alive) return;
      try {
        const now = await countMyPhotos(event.domainId, event.eventId);
        if (!alive) return;
        if (now !== null) {
          if (now > last) {
            changedAt = Date.now();
            last = now;
            /* Les photos arrivées sont VISIBLES tout de suite : c'est ce qui distingue
               une attente qui avance d'une attente qui dure. */
            await client.invalidateQueries({ queryKey: ["photos"] });
            if (alive) setState({ step: "watching", found: now });
          }
        }
      } catch {
        // Une vérification ratée n'interrompt rien : la suivante dira la même chose.
      }
      if (!alive) return;
      const encore = keepPolling({
        elapsedMs: Date.now() - started,
        sinceChangeMs: Date.now() - changedAt,
      });
      if (encore) timer = window.setTimeout(look, POLL_EVERY_MS);
      else setState({ step: "done", found: last });
    };

    timer = window.setTimeout(look, POLL_EVERY_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
    /* `state` n'est lu qu'au démarrage, pour le point de départ : le relister
       relancerait la surveillance à chaque photo trouvée. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching, event, client]);

  const removing = state.step === "removing";
  const busy =
    state.step === "sending" ||
    state.step === "searching" ||
    watching ||
    removing;

  /**
   * LA RECHERCHE, ET CE QU'ON FAIT DE SON RÉSULTAT. Elle suit l'envoi d'une photo.
   *
   * ⚠️ ELLE JETTE PLUTÔT QUE D'ANNONCER L'ÉCHEC : l'appelant sait que l'envoi, lui, a
   * réussi, et une recherche ratée ne se dit pas avec les mots d'un envoi raté.
   */
  const runSearch = async (ev: { domainId: number; eventId: number }) => {
    setState({ step: "searching" });
    const outcome = await searchMyPhotos(ev.domainId);
    /* L'album se relit dans tous les cas : même une recherche abandonnée en chemin a
       pu rattacher des photos avant qu'on cesse d'attendre. */
    await client.invalidateQueries({ queryKey: ["photos"] });
    /* ⚠️ CE QU'ON ANNONCE, C'EST SES PHOTOS EN TOUT, pas ce que cette recherche a
       ajouté. « Aucune photo de vous » sous un onglet « Mes photos (4) » : c'est ce que
       donnait le nombre de lignes créées, nul quand les photos étaient déjà là. Ce
       compte est aussi le point de départ du guet, s'il y en a un. */
    const after =
      (await countMyPhotos(ev.domainId, ev.eventId)) ?? outcome.created;
    setState(
      outcome.done
        ? { step: "done", found: after }
        : { step: "watching", found: after },
    );
    /* Le compte n'est définitif que la recherche terminée ; le guet, lui, continue. */
    return outcome.done ? after : undefined;
  };

  /**
   * ⚠️ CE QUI PART N'EST PLUS LE FICHIER CHOISI, C'EST LE CARRÉ CADRÉ. Une photo de
   * téléphone entière laissait Rekognition chercher un visage de cinquante pixels dans
   * une salle, et l'avatar rond du site en montrait le torse. Le cadrage se fait avant
   * (`Cropper`), le reste de la mécanique n'a pas bougé.
   */
  const choose = async (file: File | undefined) => {
    if (!file || !event || busy) return;
    /* Lu avant l'envoi : après, la pastille porte déjà la nouvelle photo. */
    const replacing = !!photo;
    setState({ step: "sending" });
    try {
      await setOwnPortrait(event.domainId, file);
      /* Le portrait affiché change tout de suite : c'est le seul retour immédiat que
         la personne ait sur ce qu'elle vient d'envoyer. */
      await client.invalidateQueries({ queryKey: ["photo"] });
    } catch (error) {
      setState({
        step: "failed",
        why:
          error instanceof UploadError
            ? error.message
            : /* Le serveur n'a pas trouvé de visage : ce n'est pas un envoi raté, c'est
                 la photo qui ne convient pas, et la personne peut y remédier. */
              error instanceof ApiError && error.message === "no-face"
              ? t(
                  "PORTRAIT_NO_FACE",
                  "Aucun visage n'a été reconnu sur cette photo. Choisissez une photo où votre visage est net et de face.",
                )
              : error instanceof ApiError && error.message === "many-faces"
                ? t(
                    "PORTRAIT_MANY_FACES",
                    "Plusieurs visages sur cette photo. Choisissez une photo où vous êtes seul.",
                  )
                : t(
                    "PORTRAIT_FAILED",
                    "Votre photo n'a pas pu être envoyée. Réessayez dans un instant.",
                  ),
      });
      return;
    }

    /**
     * ⚠️ UNE RECHERCHE RATÉE N'EST PAS UN ENVOI RATÉ. La photo est partie et déjà
     * affichée ; dire « votre photo n'a pas pu être envoyée » enverrait chercher une
     * autre photo pour un problème qui n'est pas là. Le bouton de relance faisait cette
     * distinction ; il a disparu, la distinction reste.
     */
    let found: number | undefined;
    try {
      found = await runSearch(event);
    } catch {
      setState({
        step: "failed",
        why: t(
          "PORTRAIT_SEARCH_FAILED",
          "La recherche n'a pas pu aboutir. Réessayez dans un instant.",
        ),
      });
    }
    /* Une fois la recherche passée, pour y joindre ce qu'elle a retrouvé : la photo de
       profil vaut consentement à la reconnaissance, le journal en garde la date. */
    logAction(event, "faceUpload", { label: faceUploadLabel(replacing, found) });
  };

  /**
   * EFFACER SA PHOTO (RGPD).
   *
   * ⚠️ L'ALBUM N'EST PAS RELU. Les photos de l'événement déjà rattachées le restent —
   * c'est ce que dit la question posée avant. Seul le portrait s'en va, donc seule la
   * pastille change (`["photo"]`, la même requête que la barre du site).
   */
  const remove = async () => {
    if (!event || typeof userId !== "number" || busy) return;
    setState({ step: "removing" });
    try {
      await removeOwnPortrait(event.domainId, userId);
      /* Le retrait du consentement : la ligne `UserFaces` est effacée, il ne reste que celle-ci. */
      logAction(event, "faceRemoval");
      await client.invalidateQueries({ queryKey: ["photo"] });
      setAsking(false);
      setState({ step: "removed" });
    } catch {
      setAsking(false);
      setState({
        step: "failed",
        why: t(
          "PORTRAIT_DELETE_FAILED",
          "Votre photo n'a pas pu être supprimée. Réessayez dans un instant.",
        ),
      });
    }
  };

  const said = () => {
    if (state.step === "sending")
      return t("PORTRAIT_SENDING", "Envoi de votre photo en cours");
    if (state.step === "searching")
      return t("PORTRAIT_SEARCHING", "Recherche de vos photos en cours");
    if (state.step === "removing")
      return t("PORTRAIT_DELETING", "Suppression de votre photo en cours");
    if (state.step === "removed")
      return t("PORTRAIT_DELETED", "Votre photo a été supprimée.");
    if (state.step === "failed") return state.why;
    if (state.step === "watching")
      return t(
        "PORTRAIT_WATCHING",
        "Recherche en cours. Vos photos apparaissent ici au fur et à mesure.",
      );
    if (state.step === "done") {
      if (state.found > 1)
        return t("PORTRAIT_FOUND_N", "$1 photos de vous dans l'album.", [
          state.found,
        ]);
      if (state.found === 1)
        return t("PORTRAIT_FOUND_ONE", "1 photo de vous dans l'album.");
      return t(
        "PORTRAIT_NONE_YET",
        "Aucune photo de vous pour le moment. Revenez après l'événement.",
      );
    }
    return photo
      ? t(
          "PORTRAIT_HAS_ONE",
          "Nous retrouvons vos photos grâce à cette image. Changez-la si elle ne vous ressemble plus.",
        )
      : t(
          "PORTRAIT_ASK",
          "Ajoutez une photo de vous, bien cadrée sur le visage : nous retrouverons vos photos dans l'album.",
        );
  };

  /**
   * RIEN À RACONTER : photo connue, rien en cours. C'est ce qui REPLIE la ligne — et non
   * plus ce qui la faisait taire. Dépliée à la demande, elle parle : dire à quoi sert cette
   * photo remplit l'espace entre la pastille et les gestes, exactement comme la ligne sans
   * photo (la pastille, une phrase, les gestes).
   */
  const nothingToSay = photo !== null && state.step === "idle";

  /**
   * LE PLI. Un seul geste, la pastille et son nom, qui déplie la ligne. Rien d'autre n'est
   * rendu : ni le sélecteur de fichier ni les fenêtres — aucun geste ne peut les ouvrir
   * d'ici, et un état qui bouge (`nothingToSay` devenu faux) déplie la ligne de lui-même.
   */
  if (photo !== null && nothingToSay && !open) {
    return (
      <div className="prt prt--folded" data-cfg="albPortrait">
        <Button
          type="button"
          variant="ghost"
          className="prt__fold"
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          <img className="prt__i prt__i--sm" src={photo} alt="" />
          {t("PORTRAIT_MANAGE", "Gérer ma photo")}
        </Button>
      </div>
    );
  }

  return (
    <div className="prt" data-cfg="albPortrait">
      {photo ? (
        <img className="prt__i" src={photo} alt="" />
      ) : (
        <span className="prt__i prt__i--none" aria-hidden="true" />
      )}

      <div className="prt__t">
        {/* `role="status"` : ce qui change ici pendant l'attente doit s'entendre, pas
            seulement se voir. */}
        <p role="status">
          {busy && <span className="spin" aria-hidden="true" />}
          {said()}
        </p>
        {/* La barre n'est pas un pourcentage, et elle ne prétend pas l'être : personne
            ne sait combien de photos vont arriver. Elle dit « ça tourne encore », ce
            qu'un texte figé ne dit pas. Décorative, donc tue pour les lecteurs
            d'écran, qui ont déjà le message ci-dessus. */}
        {watching && (
          <span className="prt__b" aria-hidden="true">
            <span />
          </span>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*"
        /* ⚠️ PAS DE `capture`. Il ouvrirait l'appareil photo frontal SANS laisser le
           choix sur plusieurs navigateurs Android, or on demande de CHOISIR sa photo,
           et la meilleure est souvent déjà dans la galerie. Le sélecteur natif propose
           les deux. */
        hidden
        onChange={(e) => {
          setPicked(e.target.files?.[0] ?? null);
          /* ⚠️ LA COMMANDE SE VIDE TOUT DE SUITE. Le même fichier choisi deux fois de
             suite ne relance aucun `change` si elle garde sa valeur — et c'est
             exactement ce qu'on fait après avoir refermé le cadrage sans valider. */
          e.target.value = "";
        }}
      />

      <div className="prt__a">
        {photo ? (
          <>
            {/* ⚠️ RÉDUITS À LEUR DESSIN, PAS RETIRÉS. Leur libellé part dans l'infobulle
                ET dans `aria-label` : ce qui n'est plus écrit doit rester dit, sinon le
                bouton n'existe que pour qui le voit. */}
            <Button
              type="button"
              variant="ghost"
              className="btn--ico"
              title={t("PORTRAIT_CHANGE", "Changer ma photo")}
              aria-label={t("PORTRAIT_CHANGE", "Changer ma photo")}
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              <Icon name="appareil" />
            </Button>

            {/* Effacer sa photo est un droit, pas une option avancée : le geste est là,
                à côté de celui qui la change, et seulement quand on sait quelle ligne
                viser (`userId`). */}
            {typeof userId === "number" && (
              <Button
                type="button"
                variant="ghost"
                className="btn--ico btn--danger"
                title={t("PORTRAIT_DELETE", "Supprimer ma photo")}
                aria-label={t("PORTRAIT_DELETE", "Supprimer ma photo")}
                disabled={busy}
                onClick={() => setAsking(true)}
              >
                <Icon name="corbeille" />
              </Button>
            )}
          </>
        ) : (
          /* Sans photo, un seul geste, et il s'écrit : c'est la seule chose à faire ici,
             et une icône seule demanderait de deviner par quoi commencer. */
          <Button
            type="button"
            variant="ghost"
            loading={busy}
            onClick={() => input.current?.click()}
          >
            <Icon name="appareil" />
            {t("PORTRAIT_CHOOSE", "Choisir ma photo")}
          </Button>
        )}
      </div>

      {/* ⚠️ LE CADRAGE DANS UNE FENÊTRE, PAS DANS LE BLOC. Le carré a besoin de place
          et le geste a besoin d'être terminé : dans la page, on fait glisser sa photo
          au-dessus de l'album, et un doigt qui dépasse du cadre fait défiler la liste. */}
      <Modal
        open={picked !== null}
        title={t("PORTRAIT_FRAME_TITLE", "Cadrer ma photo")}
        onClose={() => setPicked(null)}
      >
        {picked && (
          <Cropper
            file={picked}
            onCancel={() => setPicked(null)}
            onConfirm={(framed) => {
              /* La fenêtre se referme dès l'envoi : la suite (recherche, photos qui
                 arrivent) se raconte dans le bloc, devant l'album qu'elle remplit. */
              setPicked(null);
              void choose(framed);
            }}
          />
        )}
      </Modal>

      <Confirm
        open={asking}
        title={t("PORTRAIT_DELETE_TITLE", "Supprimer ma photo ?")}
        message={t(
          "PORTRAIT_DELETE_ASK",
          "Votre photo sera définitivement supprimée et nous cesserons de vous reconnaître sur les photos de l'événement. Les photos déjà retrouvées restent dans votre album.",
        )}
        confirmLabel={t("PORTRAIT_DELETE", "Supprimer ma photo")}
        busy={removing}
        onConfirm={() => void remove()}
        onCancel={() => setAsking(false)}
      />
    </div>
  );
}
